<template>
    <div class="bg-white shadow-md rounded sm:rounded-lg p-4 sm:p-6">
        <property-details :accountNumber="accountNumber" :property="property"
            :isRegistrationClosedOrFullyOpened="isRegistrationClosedOrFullyOpened"></property-details>

        <div class="border-t border-gray-200 mt-6 pt-5">
            <div v-if="!donation_page">

                <!-- Have you already done anything with the appraisal district? -->
                <fieldset>
                    <div>
                        <label class="block text-sm font-medium text-gray-900 mb-1">
                            Have you already done anything with the appraisal district for this property this year?
                        </label>
                        <p class="text-sm text-gray-500 mb-2">Examples: filed or started a protest, saw an offer,
                            accepted or rejected an offer, received a hearing notice, or received protest-related
                            mail/email.</p>
                        <div>
                            <select v-model="data.already_filed_protest"
                                class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full text-sm border-gray-300 rounded-md"
                                :class="{ 'border-red-500': errors.already_filed_protest }">
                                <option value="" selected disabled>Please select</option>
                                <option value="no">No</option>
                                <option value="yes">Yes</option>
                                <option value="not_sure">I'm not sure</option>
                            </select>
                        </div>
                        <div class="text-red-600 text-sm px-1 py-1" v-if="errors.already_filed_protest">
                            <i class="fa fa-exclamation-triangle"></i> {{ errors.already_filed_protest }}
                        </div>
                    </div>
                    <div v-if="['yes', 'not_sure'].includes(data.already_filed_protest)" class="mt-3">
                        <div class="bg-yellow-50 border border-yellow-300 rounded-md p-4 flex gap-3">
                            <i class="fa fa-exclamation-triangle text-yellow-500 mt-0.5 flex-shrink-0"></i>
                            <p class="text-sm text-yellow-800"><strong>Important:</strong> If you already started the
                                protest process, there may be offers, deadlines, or hearing dates we do not know about.
                                Depending on the timing, this could cause you to miss a hearing, miss a deadline, or
                                lose the opportunity to accept a prior offer from the appraisal district. We cannot
                                guarantee any level of success.</p>
                        </div>

                        <div class="text-red-600 text-sm px-1 py-1" v-if="errors.already_filed_protest_acknowledged">
                            <i class="fa fa-exclamation-triangle"></i> {{ errors.already_filed_protest_acknowledged }}
                        </div>
                    </div>
                </fieldset>

                <!-- Name listed is personal name -->
                <fieldset
                    v-if="!user.userProperty || (user.userProperty && !user.userProperty.property_representative)">
                    <div class="mt-7">
                        <label class="block text-sm font-medium text-gray-900 mb-2">
                            Is <u>your personal name</u> listed above as owner?
                        </label>

                        <div>
                            <select v-model="data.is_owner"
                                class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full text-sm border-gray-300 rounded-md"
                                :class="{ 'border-red-500': errors.is_owner }">
                                <option value="" selected disabled>Please select</option>
                                <option value="yes">Yes, it is my personal name</option>
                                <option value="owner_but_not_listed">My name is not listed above, but I am an owner
                                </option>
                                <option value="entity">The name above is a trust, business name, or entity</option>
                            </select>
                        </div>

                        <div class="text-red-600 text-sm px-1 py-1" v-if="errors.is_owner">
                            <i class="fa fa-exclamation-triangle"></i> {{ errors.is_owner }}
                        </div>
                    </div>
                </fieldset>

                <fieldset v-if="data.is_owner == 'owner_but_not_listed'">
                    <div class="mt-7">
                        <label class="block text-sm font-medium text-gray-900 mb-2">
                            Visit TAD.org and look up your property to verify that you name appears on the account (
                            <a href="https://www.youtube.com/watch?v=H7FzyNapOjg" target="_blank">
                                class="text-indigo-600 underline">How to look up your account on TAD.org</a> )
                        </label>


                        <div>
                            <select v-model="data.verify_owner_on_tad"
                                class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full text-sm border-gray-300 rounded-md"
                                :class="{ 'border-red-500': errors.verify_owner_on_tad }">
                                <option value="" selected disabled>Please select</option>
                                <option value="yes">Yes, my name appears on TAD.org</option>
                                <option value="no">No, my name does not appear on TAD.org</option>
                            </select>
                        </div>

                        <div class="text-red-600 text-sm px-1 py-1" v-if="errors.verify_owner_on_tad">
                            <i class="fa fa-exclamation-triangle"></i> {{ errors.verify_owner_on_tad }}
                        </div>
                    </div>
                </fieldset>

                <!--  What year did you begin ownership of this property?  -->
                <fieldset>
                    <div class="mt-7">
                        <label class="block text-sm font-medium text-gray-900 mb-2">
                            What year did you begin ownership of this property?
                        </label>

                        <div>
                            <input type="number" name="purchase_year" v-model="data.purchase_year" min=1900
                                :max="current_year" placeholder="Please enter"
                                class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full text-sm border-gray-300 rounded-md"
                                :class="{ 'border-red-500': errors.purchase_year }" />
                        </div>
                    </div>

                    <div class="text-red-600 text-sm px-1 py-1" v-if="errors.purchase_year">
                        <i class="fa fa-exclamation-triangle"></i> {{ errors.purchase_year }}
                    </div>
                </fieldset>

                <!-- Do you plan on selling this property within the next 12 months -->
                <fieldset>
                    <div class="mt-7">
                        <label class="block text-sm font-medium text-gray-900 mb-2">
                            Do you plan on selling this property within the next 12 months?
                        </label>

                        <div>
                            <select v-model="data.selling"
                                class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full text-sm border-gray-300 rounded-md"
                                :class="{ 'border-red-500': errors.selling }">
                                <option value="" selected disabled>Please select</option>
                                <option value="Yes"> Yes </option>
                                <option value="No"> No </option>
                                <option value="Maybe"> Maybe </option>
                            </select>
                        </div>
                    </div>

                    <div class="mt-3" v-if="['Maybe', 'Yes'].includes(data.selling)">
                        <div class="mb-2">
                            <label class="-mb-1 block font-medium text-gray-900 text-sm">
                                If {{ data.selling == 'Maybe' ? 'maybe' : 'yes' }}, tell us what you are thinking
                                (optional)
                            </label>
                            <span class="text-gray-700 text-xs">This helps us understand your situation. A timeline is
                                fine if you have one.</span>
                        </div>
                        <input type="text" v-model="data.selling_additional_details"
                            :placeholder="data.selling == 'Maybe' ? 'Example: waiting on rates, job change possible, downsizing, considering renting it out, maybe in 6 to 12 months' : 'Example: already talking to a Realtor, relocating, upsizing, want to sell after school year, etc.'"
                            class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full text-sm border-gray-300 rounded-md" />

                    </div>

                    <div class="text-red-600 text-sm px-1 py-1" v-if="errors.selling">
                        <i class="fa fa-exclamation-triangle"></i> {{ errors.selling }}
                    </div>
                </fieldset>

                <!-- How long do you plan on owning this property -->
                <fieldset v-if="data.selling == 'No'">
                    <div class="mt-7">
                        <label class="block text-sm font-medium text-gray-900 mb-2">
                            How long do you plan on owning this property?
                        </label>

                        <div>
                            <select v-model="data.years_to_keep_property"
                                class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full text-sm border-gray-300 rounded-md"
                                :class="{ 'border-red-500': errors.years_to_keep_property }">
                                <option value="" selected disabled>Please select</option>
                                <option value="1-2 Years"> 1-2 Years </option>
                                <option value="3-10 Years"> 3-10 Years </option>
                                <option value="10+ Years"> 10+ Years </option>
                            </select>
                        </div>
                    </div>

                    <div class="text-red-600 text-sm px-1 py-1" v-if="errors.years_to_keep_property">
                        <i class="fa fa-exclamation-triangle"></i> {{ errors.years_to_keep_property }}
                    </div>
                </fieldset>

                <!-- Why do you feel like your house is being over valued by the appraisal district? -->
                <fieldset>
                    <div class="mt-7">
                        <label class="block text-sm font-medium text-gray-900 mb-3">
                            Why do you feel like your house is being over valued by the appraisal district?
                            <span class="text-gray-400"> (Check all that apply) </span>
                        </label>

                        <div class="space-y-4"
                            :class="{ 'border border-red-500 p-1 rounded': errors.house_overvalued_reason }">
                            <div class="flex items-center">
                                <input id="house_overvalued_1" type="checkbox" v-model="data.house_overvalued_reason"
                                    value="My house isn’t in good condition"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
                                <label for="house_overvalued_1" class="ml-3 block text-sm font-medium text-gray-700">
                                    My house isn’t in good condition
                                </label>
                            </div>

                            <div class="flex items-center">
                                <input id="house_overvalued_2" type="checkbox" v-model="data.house_overvalued_reason"
                                    value="My house would not sell at the price the appraisal district is valuing my house at"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
                                <label for="house_overvalued_2" class="ml-3 block text-sm font-medium text-gray-700">
                                    My house would not sell at the price the appraisal district is valuing my house at
                                </label>
                            </div>

                            <div class="flex items-center">
                                <input id="house_overvalued_3" type="checkbox" v-model="data.house_overvalued_reason"
                                    value="Similar houses are being taxed at a lower price"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
                                <label for="house_overvalued_3" class="ml-3 block text-sm font-medium text-gray-700">
                                    Similar houses are being taxed at a lower price
                                </label>
                            </div>

                            <div class="flex items-center">
                                <input id="house_overvalued_4" type="checkbox" v-model="data.house_overvalued_reason"
                                    value="I don’t know for sure. I just know it's smart to protest"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
                                <label for="house_overvalued_4" class="ml-3 block text-sm font-medium text-gray-700">
                                    I don’t know for sure. I just know it's smart to protest.
                                </label>
                            </div>
                        </div>
                    </div>

                    <div class="text-red-600 text-sm px-1 py-1" v-if="errors.house_overvalued_reason">
                        <i class="fa fa-exclamation-triangle"></i> {{ errors.house_overvalued_reason }}
                    </div>
                </fieldset>

                <!-- How would you describe the condition of your property? -->
                <fieldset>
                    <div class="mt-7">
                        <label class="block text-sm font-medium text-gray-900 mb-2">
                            How would you describe the condition of your property?
                        </label>

                        <div>
                            <select v-model="data.condition"
                                class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full text-sm border-gray-300 rounded-md"
                                :class="{ 'border-red-500': errors.condition }">
                                <option value="" selected disabled>Please Select</option>
                                <option value="Excellent"> Excellent - Recently constructed or completely updated and
                                    remodeled</option>
                                <option value="Good"> Good - Everything is functional with some wear and tear </option>
                                <option value="Average"> Average - Minor and cosmetic repairs are needed </option>
                                <option value="Fair"> Fair - Obvious major repairs needed to major components of the
                                    house
                                </option>
                                <option value="Poor"> Poor - Defects affect the safety and soundness. It may be
                                    uninhabitable </option>
                            </select>
                        </div>
                    </div>

                    <div class="text-red-600 text-sm px-1 py-1" v-if="errors.condition">
                        <i class="fa fa-exclamation-triangle"></i> {{ errors.condition }}
                    </div>
                </fieldset>

                <div v-if="['yes', 'not_sure'].includes(data.already_filed_protest)" class="border-t mt-4 sm:mt-6 pt-4 sm:pt-6">
                    <div class="mt-3"
                        :class="{ 'border border-red-500 p-2 rounded': errors.already_filed_protest_acknowledged }">
                        <label class="flex items-start gap-3 cursor-pointer">
                            <input type="checkbox" v-model="data.already_filed_protest_acknowledged"
                                class="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 flex-shrink-0" />
                            <span class="text-sm text-gray-700 font-medium">I understand and agree to immediately send any protest
                                notices, hearing dates, offers, emails, letters, or messages I received from the appraisal
                                district to protest@chandlercrouch.com. I also understand Chandler Crouch, this company, and
                                FreeTaxProtest.com cannot guarantee any reduction, result, or level of success.</span>
                        </label>
                    </div>
                </div>
            </div>
            <donation-page :user-property="userProperty" v-else :donationPageData="donationPageData" :errors="errors"
                :isUserSignUp="true"></donation-page>

        </div>

        <div class="w-full mt-4 sm:mt-6 text-center ">
            <button v-if="!donation_page"
                class="inline-flex justify-center py-2 px-6 border border-transparent shadow-sm text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                @click="nextPage()">Continue to authorization</button>
            <button v-else :disabled="isProcessing" type="button" @click="submitHandler()"
                class="inline-flex justify-center py-2 px-6 border border-transparent shadow-sm text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                <i v-if="isProcessing" class="fa fa-spinner fa-spin p-1"></i>
                <span v-if="!user.userProperty">Authorize and continue</span>
            </button>
        </div>
    </div>
</template>
<script>
import ProtestRequestApi from "@/services/api/ProtestRequest.js";
import PropertyDetails from "./partial/PropertyDetails.vue";
import DonationPage from "./partial/Donation.vue";
import Bugsnag from '@/bugsnag.js';

export default {
    props: ["accountNumber", "property", "userProperty", "user", "isRegistrationClosedOrFullyOpened"],

    components: {
        PropertyDetails,
        DonationPage
    },

    data() {
        return {
            data: {},
            errors: {},
            isProcessing: false,
            current_year: new Date().getFullYear(),
            donation_page: false,
            donationPageData: {
                donation_amount: '',
                willing_to_hire_realtor: '',
                agree_to_terms_and_conditions: '',
                real_estate_additional_comments: ''


            },
        };
    },

    created() {

        // ProtestRequestApi.getCurrentStep(
        //     this.accountNumber
        // ).then((res) => {
        //     if (res.data.step && res.data.step != "step4"){
        //          window.location.href = res.data.stepUrl ;
        //     }
        // });


        this.init();
    },

    methods: {
        init() {
            this.data = {
                ...this.data,
                ...{
                    already_filed_protest: "",
                    already_filed_protest_acknowledged: false,
                    is_owner: "",
                    verify_owner_on_tad: "",
                    purchase_year: "",
                    selling: "",
                    years_to_keep_property: "",
                    selling_additional_details: "",
                    house_overvalued_reason: [],
                    agree_to_terms_and_conditions: "",
                    condition: "",
                },
            };
        },
        nextPage() {
            if (this.validateData()) {
                console.log(this.data.agree_to_terms_and_conditions)
                flash(
                    "A required question needs to be answered. Please review your answers below",
                    "error"
                );
                return;
            }
            this.donation_page = true;
            window.scrollTo(0, 0)
            return;
        },

        submitHandler() {

            Object.assign(this.data, this.donationPageData)
            if (this.validateData()) {
                flash(
                    "A required question needs to be answered. Please review your answers below",
                    "error"
                );
                return;
            }
            let donationPageError = false;
            if (this.donationPageData.agree_to_terms_and_conditions == null || this.donationPageData.agree_to_terms_and_conditions == "") {
                this.errors.agree_to_terms_and_conditions = "Please select an option";
                donationPageError = true;
            }
            if (
                this.donationPageData.donation_amount == null ||
                this.donationPageData.donation_amount == ""
            ) {
                this.errors.donation_amount = "Please enter 0 or more amount.";
                donationPageError = true;
            }
            if (donationPageError) {
                flash(
                    "A required question needs to be answered. Please review your answers below",
                    "error"
                );
                return;
            }

            this.isProcessing = true;
            window.blockUI();

            this.data.accountNumber = this.accountNumber;

            ProtestRequestApi.create(this.data)
                .then((res) => {

                    if (res.data.error == false) {
                        window.location.href = res.data.redirectUrl;
                    } else {
                        this.isProcessing = false;
                        alert("ERR1044: " + res.data.message);
                    }
                })
                .catch((error_response) => {
                    Bugsnag.notify(error_response);
                    alert("ERR1044: System Error. Please contact administrator");
                    console.log(error_response);
                })
                .finally(() => {
                    this.isProcessing = false;
                    window.unblockUI();
                });
        },

        validateData() {
            this.errors = {};
            let error = false;

            if (!this.data.already_filed_protest) {
                this.errors.already_filed_protest = "Please select an option";
                error = true;
            }

            if (
                ['yes', 'not_sure'].includes(this.data.already_filed_protest) &&
                !this.data.already_filed_protest_acknowledged
            ) {
                this.errors.already_filed_protest_acknowledged = "You must acknowledge this to continue";
                error = true;
            }

            if (
                !this.data.is_owner &&
                !this.user.userProperty?.property_representative
            ) {
                this.errors.is_owner = "Please select an option";
                error = true;
            }

            if (
                this.data.is_owner == "owner_but_not_listed" &&
                !this.data.verify_owner_on_tad
            ) {
                this.errors.verify_owner_on_tad = "Please select an option";
                error = true;
            }

            if (!this.data.purchase_year && !this.user.userProperty?.purchase_year) {
                this.errors.purchase_year = "Please enter purchase year";
                error = true;
            }
            if (
                this.data.purchase_year &&
                (this.data.purchase_year > this.current_year ||
                    this.data.purchase_year < 1900)
            ) {
                this.errors.purchase_year =
                    "Purchase year must be between 1900 and " + this.current_year;
                error = true;
            }

            if (!this.data.selling) {
                this.errors.selling = "Please select an option";
                error = true;
            }

            if (this.data.selling == "No" && !this.data.years_to_keep_property) {
                this.errors.years_to_keep_property = "Please select an option";
                error = true;
            }

            // if (
            //     this.data.selling == "Maybe" &&
            //     !this.data.selling_additional_details
            // ) {
            //     this.errors.selling = "Please enter additional details";
            //     error = true;
            // }

            if (!this.user.userProperty) {
                if (this.data.house_overvalued_reason.length == 0) {
                    this.errors.house_overvalued_reason =
                        "Please select atleast 1 option";
                    error = true;
                }

                if (this.data.condition == "") {
                    this.errors.condition = "Please select an option";
                    error = true;
                }

            }

            return error;
        },
        donationPage(data) {
            this.donationPageData.donation_amount = data.donation_amount;
            this.donationPageData.willing_to_hire_realtor = data.willing_to_hire_realtor;
            this.donationPageData.agree_to_terms_and_conditions = data.agree_to_terms_and_conditions;
            this.donationPageData.real_estate_additional_comments = data.real_estate_additional_comments;


        }
    },
};
</script>
<style>
option[value=""][disabled] {
    color: gray;
}
</style>
